/**
 * Breton catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Breton (Brezhoneg) at a mathematician's register. **Warrant → «Gwarant»**.
 * CLDR cardinal categories are one / two / few / many / other, so `frameActive` declares
 * exactly those (plus an explicit `=0`).
 *
 * NOTE (honesty over guessing): Breton has the least-settled modern mathematical/epistemic
 * vocabulary of this batch and intricate mutation after numerals. This catalog is a careful
 * best-effort draft and the WHOLE of it is flagged "expert-review suggested" in
 * `Agent9_REPORT.md` — a flagged draft is more honest than a confident error, and per
 * INV-STABLE-ID any later correction changes only the string and leaves every verdict fixed.
 * W/M grades stay Latin (`::numbering-system/latn`).
 */
import { P2 } from '../message-ids-phase2.js';
export const BR_CATALOG = {
    locale: 'br',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Enklask',
        [P2.sectionClaims]: 'Lavaroù',
        [P2.sectionIntegrity]: 'Klokted',
        [P2.axisWarrantLabel]: 'Gwarant',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Amprouek',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Termenadur} interp {Displegadur} testable {Amprouadus} tested {Amprouet} refuted {Disprouet} program {Programm enklask} philos {Prederouriel} other {Dianav}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Live nerzh {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Ergerzhus',
        [P2.stateRefuted]: 'Disprouet',
        [P2.stateOutside]: 'Er-maez eus ar skop',
        [P2.verdictConflict]: 'E gwrthstriv gant {constraints}',
        [P2.verdictOutside]: 'Ezhomm {missing}',
        [P2.lossRegisterPlain]: 'Eeunaet e yezh boutin — lod eus ar resisted a vez laosket a-gostez.',
        [P2.lossRegisterKid]: 'Eeunaet evit lennerien yaouank — kalz munudoù a vez laosket a-gostez.',
        [P2.lossProfileDetail]: 'Krennet eo amañ ar profil klok Gwarant / Amprouek / Modal.',
        [P2.lossConflictDetail]: 'Berraet eo munudoù ar gwrthstriv er gwel-mañ.',
        [P2.laneVerified]: 'Gwiriet',
        [P2.laneUnverified]: 'Diwiriet',
        [P2.laneConflict]: 'Gwrthstriv prouet',
        [P2.constraintLogic]: 'Poelloniezh',
        [P2.constraintLightBends]: 'Plegañ a ra ar gouloù en ur park gravitael',
        [P2.constraintParallelPostulate]: 'Postuladenn al linennoù kenstur',
        [P2.claimScalarGravity]: 'Teorienn skalarel ar gravitañ',
        [P2.claimNonEuclidean]: 'Mentoniezh nann-euklidek',
        [P2.claimSubstrateSr]: 'Displegadur is-gwiskad eus an emdroadelezh ispisial',
        [P2.edgeExtObservation]: 'Evezhiadenn diavaez',
        [P2.edgeResourceSample]: 'Andon jedoniel rannet',
        [P2.edgeHeuristicSample]: 'Sturiadur heuristek',
        [P2.edgeSharedSample]: 'Diazez rannet',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Bevennoù',
        [P2.sectionEdges]: 'Riblennoù',
        [P2.sectionFrame]: 'Stern',
        [P2.sectionExposure]: 'Diskuliañ',
        [P2.statusAdoptedFormal]: 'Degemeret (furmel)',
        [P2.statusAdoptedInterpretive]: 'Degemeret (displegadel)',
        [P2.statusSuspended]: 'Ehanet',
        [P2.edgeEffectConstraint]: 'Sevel a ra ur vevenn (gall disprouiñ)',
        [P2.edgeEffectResource]: 'Andon — efed ebet war ar varnedigezhioù',
        [P2.edgeEffectHeuristic]: 'Heuristek — efed ebet war ar varnedigezhioù',
        [P2.edgeEffectShared]: 'Diazez rannet — efed ebet war ar varnedigezhioù',
        [P2.frameActive]: '{count, plural, =0 {Bevenn oberiant ebet} one {# vevenn oberiant} two {# vevenn oberiant} few {# bevenn oberiant} many {# a vevennoù oberiant} other {# bevenn oberiant}}',
        [P2.exposureNone]: "Diankendalc'h displegadel ebet",
        [P2.exposureDep]: 'Diazezet war {id}',
        [P2.laneTrackMachine]: 'Mekanik',
        [P2.laneTrackExpert]: 'Arbennigour',
        [P2.laneTrackCommunity]: 'Kumuniezh',
        [P2.expertAudited]: 'Gwiriekaet',
        [P2.expertInProgress]: 'War ober',
        [P2.expertNotReviewed]: "N'eo ket bet adwelet",
        [P2.communityFlags]: 'Bez ez eus merkoù',
        [P2.communityNone]: 'Merk ebet',
        [P2.controlChallenge]: 'Dael',
        [P2.controlActivate]: 'Gweredekaat',
        [P2.controlMakeInterpretive]: 'Lakaat da zisplegadel',
        [P2.controlImportEdge]: 'Enporzhiañ ur riblenn',
        [P2.controlPromote]: 'Uhelaat',
        [P2.controlUndo]: 'Dizober',
        [P2.controlRedo]: 'Adober',
        [P2.controlRegister]: 'Enrollañ',
        [P2.promoteRejected]: "Uhelaat nac'het",
        [P2.promoteAccepted]: 'Uhelaat asantet',
        [P2.promoteReasonRefutationTerminal]: "Ne c'hall ket ur prouenn yac'haat un disprouadenn.",
        [P2.promoteReasonKeystone]: 'Ezhomm en deus un uhelaat-mein-toull eus skor amprouennel.',
        [P2.promoteReasonEmpiricalNotFixed]: "N'eo ket fichet ar seurt amprouek.",
        [P2.promoteReasonModalNotFixed]: "N'eo ket fichet an derez modal.",
        [P2.promoteReasonWarrantNotImproved]: "N'eo ket bet gwellaet an derez gwarant.",
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=br.js.map